import React, { useState } from 'react';
import { FormField } from './FormField';
import { SearchIcon, ShoppingBagIcon, PlusIcon, MinusIcon, XIcon, StoreIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface Shop {
  id: number;
  name: string;
  description: string;
  logo: string;
}
interface Product {
  id: number;
  shopId: number;
  name: string;
  price: number;
  description: string;
  image: string;
  category: string;
}
interface CartItem {
  productId: number;
  quantity: number;
  product: Product;
}
interface ShopProductsPaymentProps {
  onExpiryDateChange?: (date: string) => void;
}
export function ShopProductsPayment({
  onExpiryDateChange
}: ShopProductsPaymentProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedShopId, setSelectedShopId] = useState<number | null>(1); // Default to first shop
  // Mock shops data
  const shops: Shop[] = [{
    id: 1,
    name: 'Urban Outfitters',
    description: 'Contemporary clothing and lifestyle products',
    logo: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    id: 2,
    name: 'Tech Haven',
    description: 'Latest electronics and gadgets',
    logo: 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    id: 3,
    name: 'Home Essentials',
    description: 'Quality products for your home',
    logo: 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }];
  // Mock products data
  const products: Product[] = [
  // Urban Outfitters products
  {
    id: 101,
    shopId: 1,
    name: 'Basic T-Shirt',
    price: 19.99,
    description: 'Comfortable cotton t-shirt',
    image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Clothing'
  }, {
    id: 102,
    shopId: 1,
    name: 'Slim Fit Jeans',
    price: 49.99,
    description: 'Classic blue denim jeans',
    image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Clothing'
  }, {
    id: 103,
    shopId: 1,
    name: 'Leather Wallet',
    price: 29.99,
    description: 'Genuine leather wallet',
    image: 'https://images.unsplash.com/photo-1627123424574-724758594e93?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Accessories'
  },
  // Tech Haven products
  {
    id: 201,
    shopId: 2,
    name: 'Premium Headphones',
    price: 89.99,
    description: 'Noise cancelling headphones',
    image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Electronics'
  }, {
    id: 202,
    shopId: 2,
    name: 'Smart Watch',
    price: 199.99,
    description: 'Fitness and health tracking',
    image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Electronics'
  }, {
    id: 203,
    shopId: 2,
    name: 'Wireless Earbuds',
    price: 129.99,
    description: 'True wireless Bluetooth earbuds',
    image: 'https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Electronics'
  },
  // Home Essentials products
  {
    id: 301,
    shopId: 3,
    name: 'Ceramic Coffee Mug',
    price: 12.99,
    description: 'Handmade ceramic mug',
    image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Kitchen'
  }, {
    id: 302,
    shopId: 3,
    name: 'Decorative Throw Pillow',
    price: 24.99,
    description: 'Soft decorative pillow for your couch',
    image: 'https://images.unsplash.com/photo-1592078615290-033ee584e267?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Home Decor'
  }, {
    id: 303,
    shopId: 3,
    name: 'Scented Candle Set',
    price: 34.99,
    description: 'Set of 3 scented soy candles',
    image: 'https://images.unsplash.com/photo-1602523069662-0b1ea6503f14?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Home Decor'
  }];
  // Filter products by selected shop and search term
  const filteredProducts = products.filter(product => (selectedShopId === null || product.shopId === selectedShopId) && (searchTerm === '' || product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.category.toLowerCase().includes(searchTerm.toLowerCase()) || product.description.toLowerCase().includes(searchTerm.toLowerCase())));
  const addToCart = (product: Product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.productId === product.id);
      if (existingItem) {
        return prevCart.map(item => item.productId === product.id ? {
          ...item,
          quantity: item.quantity + 1
        } : item);
      } else {
        return [...prevCart, {
          productId: product.id,
          quantity: 1,
          product
        }];
      }
    });
  };
  const removeFromCart = (productId: number) => {
    setCart(prevCart => prevCart.filter(item => item.productId !== productId));
  };
  const updateQuantity = (productId: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    setCart(prevCart => prevCart.map(item => item.productId === productId ? {
      ...item,
      quantity: newQuantity
    } : item));
  };
  const totalAmount = cart.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const selectedShop = shops.find(shop => shop.id === selectedShopId) || null;
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  return <div className="space-y-6" data-id="element-2222">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2223">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-2224">
          <StoreIcon size={18} className="mr-2 text-blue-600" data-id="element-2225" />
          Shop products payment
        </h2>
        <FormField label="Select shop" tooltip="Choose a shop to view their products" data-id="element-2226">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-2227">
            {shops.map(shop => <div key={shop.id} className={`border rounded-md p-3 cursor-pointer transition-colors ${selectedShopId === shop.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => setSelectedShopId(shop.id)} data-id="element-2228">
                <div className="flex items-center" data-id="element-2229">
                  <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-2230">
                    <img src={shop.logo} alt={shop.name} className="w-full h-full object-cover" data-id="element-2231" />
                  </div>
                  <div className="ml-3" data-id="element-2232">
                    <h4 className="font-medium text-gray-800" data-id="element-2233">{shop.name}</h4>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-2234">
                      {shop.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </FormField>
      </div>
      {selectedShop && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2235">
          <div className="flex items-center mb-4" data-id="element-2236">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 mr-2" data-id="element-2237">
              <img src={selectedShop.logo} alt={selectedShop.name} className="w-full h-full object-cover" data-id="element-2238" />
            </div>
            <h3 className="text-md font-medium text-gray-700" data-id="element-2239">
              {selectedShop.name} Products
            </h3>
          </div>
          <FormField label="Search products" tooltip={`Search for products in ${selectedShop.name}`} data-id="element-2240">
            <div className="relative" data-id="element-2241">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2242">
                <SearchIcon size={18} className="text-gray-400" data-id="element-2243" />
              </div>
              <input type="text" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search by name, category, or description" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2244" />
            </div>
          </FormField>
          <div className="mt-4 mb-6" data-id="element-2245">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-2246">
              {filteredProducts.length > 0 ? filteredProducts.map(product => <div key={product.id} className="border border-gray-200 rounded-md p-3 hover:border-blue-300 transition-colors" data-id="element-2247">
                    <div className="flex" data-id="element-2248">
                      <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-gray-100" data-id="element-2249">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-2250" />
                      </div>
                      <div className="ml-3 flex-1" data-id="element-2251">
                        <div className="flex justify-between" data-id="element-2252">
                          <h4 className="font-medium text-gray-800" data-id="element-2253">
                            {product.name}
                          </h4>
                          <span className="text-gray-700" data-id="element-2254">
                            ${product.price.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-2255">
                          {product.description}
                        </p>
                        <div className="mt-2 flex justify-between items-center" data-id="element-2256">
                          <span className="text-xs text-gray-500" data-id="element-2257">
                            {product.category}
                          </span>
                          <button type="button" onClick={() => addToCart(product)} className="px-3 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md text-xs font-medium flex items-center" data-id="element-2258">
                            <PlusIcon size={12} className="mr-1" data-id="element-2259" />
                            Add to cart
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>) : <div className="col-span-2 text-center py-8 bg-gray-50 rounded-md" data-id="element-2260">
                  <SearchIcon size={24} className="mx-auto text-gray-400 mb-2" data-id="element-2261" />
                  <p className="text-gray-500" data-id="element-2262">No products found</p>
                  <p className="text-sm text-gray-400 mt-1" data-id="element-2263">
                    Try a different search term
                  </p>
                </div>}
            </div>
          </div>
        </div>}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2264">
        <div className="flex justify-between items-center mb-3" data-id="element-2265">
          <h3 className="text-sm font-medium text-gray-700" data-id="element-2266">
            Selected products
          </h3>
          <div className="text-sm font-medium text-gray-800" data-id="element-2267">
            Total: ${totalAmount.toFixed(2)}
          </div>
        </div>
        {cart.length > 0 ? <div className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-2268">
            {cart.map(item => <div key={item.productId} className="p-3 flex items-center" data-id="element-2269">
                <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0 bg-gray-100" data-id="element-2270">
                  <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" data-id="element-2271" />
                </div>
                <div className="ml-3 flex-1" data-id="element-2272">
                  <div className="flex justify-between" data-id="element-2273">
                    <h4 className="font-medium text-gray-800" data-id="element-2274">
                      {item.product.name}
                    </h4>
                    <button type="button" onClick={() => removeFromCart(item.productId)} className="text-gray-400 hover:text-red-500" data-id="element-2275">
                      <XIcon size={16} data-id="element-2276" />
                    </button>
                  </div>
                  <div className="flex justify-between items-center mt-1" data-id="element-2277">
                    <div className="flex items-center border border-gray-200 rounded-md" data-id="element-2278">
                      <button type="button" onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="px-2 py-1 text-gray-500 hover:text-gray-700" data-id="element-2279">
                        <MinusIcon size={14} data-id="element-2280" />
                      </button>
                      <span className="px-2 text-sm" data-id="element-2281">{item.quantity}</span>
                      <button type="button" onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-2 py-1 text-gray-500 hover:text-gray-700" data-id="element-2282">
                        <PlusIcon size={14} data-id="element-2283" />
                      </button>
                    </div>
                    <span className="text-sm text-gray-700" data-id="element-2284">
                      ${(item.product.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>)}
          </div> : <div className="text-center py-6 bg-gray-50 rounded-md border border-gray-200" data-id="element-2285">
            <ShoppingBagIcon size={24} className="mx-auto text-gray-400 mb-2" data-id="element-2286" />
            <p className="text-gray-500" data-id="element-2287">No products selected</p>
            <p className="text-sm text-gray-400 mt-1" data-id="element-2288">
              Select products from the shop above
            </p>
          </div>}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2289">
        <FormField label="Customer reference" tooltip="Reference for your customer" data-id="element-2290">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Reference ID" data-id="element-2291" />
        </FormField>
      </div>
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} data-id="element-2292" />
    </div>;
}